/** Shared deterministic geometry for composed movement modules. Surfaces are
 * entities carrying the `body.surface` module (dynamic tag `surface`). */
import type { ModuleCtx } from './module.js';
import { deltaX, type EntityV1, type SignalV1, type WorldV1 } from './world.js';

export type SurfaceMode = 'solid' | 'oneWay';
export interface SurfaceView { entity: EntityV1; mode: SurfaceMode; top: number; bottom: number; left: number; right: number; bumpable: boolean }

const EPS = 0.001;

/** Wrap (when the world wraps) or clamp an entity's centre into the world. */
export function confineX(world: WorldV1, e: EntityV1): 'wrapped' | 'clamped' | null {
  const w = world.bounds.w;
  if (world.bounds.wrapX) {
    if (e.x < 0) { e.x += w; return 'wrapped'; }
    if (e.x >= w) { e.x -= w; return 'wrapped'; }
    return null;
  }
  const min = e.w / 2, max = w - e.w / 2;
  if (e.x < min) { e.x = min; return 'clamped'; }
  if (e.x > max) { e.x = max; return 'clamped'; }
  return null;
}

/** Visible surfaces (hidden ones — a spent POW — no longer collide). */
export function surfaces(ctx: ModuleCtx<unknown>): SurfaceView[] {
  return ctx.entities({ tag: 'surface' }).map(entity => {
    const config = ctx.peerConfig(entity, 'body.surface')!;
    return { entity, mode: config.mode as SurfaceMode, bumpable: config.bumpable === true,
      top: entity.y - entity.h, bottom: entity.y, left: entity.x - entity.w / 2, right: entity.x + entity.w / 2 };
  });
}

/** Does a body of half-width `half` centred at x horizontally overlap s? */
export function spans(world: WorldV1, s: SurfaceView, x: number, half: number): boolean {
  const dx = Math.abs(deltaX(world, s.entity.x, x));
  return dx <= s.entity.w / 2 + half;
}

/** The highest surface top crossed by feet moving prevFeet → nextFeet. */
export function landingSurface(world: WorldV1, list: readonly SurfaceView[], x: number, half: number,
  prevFeet: number, nextFeet: number): SurfaceView | null {
  let best: SurfaceView | null = null;
  for (const s of list) {
    if (!spans(world, s, x, half)) continue;
    if (prevFeet <= s.top + EPS && nextFeet >= s.top - EPS && (!best || s.top < best.top)) best = s;
  }
  return best;
}

/** The lowest surface underside crossed by a head moving prevHead → nextHead
 * (half-open: a head resting exactly on an underside crosses it once). */
export function headSurface(world: WorldV1, list: readonly SurfaceView[], x: number, half: number,
  prevHead: number, nextHead: number): SurfaceView | null {
  let best: SurfaceView | null = null;
  for (const s of list) {
    if (!spans(world, s, x, half)) continue;
    if (prevHead >= s.bottom && nextHead < s.bottom && (!best || s.bottom > best.bottom)) best = s;
  }
  return best;
}

/** The surface whose top the feet rest on, if any. */
export function supportSurface(world: WorldV1, list: readonly SurfaceView[], x: number, half: number, feet: number): SurfaceView | null {
  let best: SurfaceView | null = null;
  for (const s of list) {
    if (Math.abs(feet - s.top) <= 0.03 && spans(world, s, x, half) && (!best || s.top < best.top)) best = s;
  }
  return best;
}

/** The surface whose top is the nearest standing level below `belowY` at x
 * (the floor under a platform) — used by solver hints. */
export function standingLevelBelow(world: WorldV1, list: readonly SurfaceView[], x: number, belowY: number): SurfaceView | null {
  let best: SurfaceView | null = null;
  for (const s of list) {
    if (s.top <= belowY + EPS || !spans(world, s, x, 0)) continue;
    if (!best || s.top < best.top) best = s;
  }
  return best;
}

export function approach(value: number, target: number, amount: number): number {
  if (value < target) return Math.min(target, value + amount);
  if (value > target) return Math.max(target, value - amount);
  return target;
}

export function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}

/** Product of every `speedMul` any module exposes on the entity (the
 * documented cross-module speed protocol: modules publish, movers consume). */
export function speedMultiplier(e: EntityV1): number {
  let mul = 1;
  for (const key of Object.keys(e.c).sort()) {
    const v = e.c[key]!.speedMul;
    if (typeof v === 'number') mul *= v;
  }
  return mul;
}

/** The documented push protocol: the velocity (world units per SECOND) that
 * rules carrying `e` without moving it — a gust band, a current — add to its
 * own motion this tick. Such a rule raises `ctx.signal('push', e, itself,
 * { vx, vy })` about `e` on EVERY tick it carries it, in a stage before
 * `move`; a movement module that honours pushes passes `ctx.signals('push')`
 * here and steps by its own velocity plus this sum, through its own clamps
 * and collisions, so the push stops the tick the rule stops raising it.
 * Every body that moves by a velocity honours it (a lane-locked one takes the
 * `vx` only); a position-driven one (a cursor, a placed marker, a rider on a
 * host, a scripted path, a static body) ignores it — the module's "reacts
 * to: push" says which. A rule never adds to `vx`/`vy` every tick
 * instead: those are the movement module's own state, eased towards the
 * steered speed each tick, and a per-tick add is an acceleration. */
export function pushVelocity(pushes: readonly SignalV1[], e: EntityV1): { vx: number; vy: number } {
  let vx = 0, vy = 0;
  for (const s of pushes) {
    if (s.target !== e.id) continue;
    if (typeof s.data.vx === 'number') vx += s.data.vx;
    if (typeof s.data.vy === 'number') vy += s.data.vy;
  }
  return { vx, vy };
}

/** The documented hold protocol: a module that takes over an entity's
 * position (a ladder, a ride, a daze) publishes `hold: true` in its own
 * component data; movement modules skip integration while any module holds
 * the entity. Returns the holding module id other than `except`, or null. */
export function heldBy(e: EntityV1, except?: string): string | null {
  for (const key in e.c) {
    if (key !== except && e.c[key]!.hold === true) return key;
  }
  return null;
}
